import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  FileText,
  PenTool,
  BarChart3,
  Gift,
  Shield,
  BookOpen,
  CheckCircle,
  PartyPopper,
  ArrowRight
} from 'lucide-react';

// Tour steps configuration
const TOUR_STEPS = [
  {
    id: 'welcome',
    title: '¡Bienvenido a NotaryOS!',
    description: 'Tu guardián legal autónomo. Te guiaré paso a paso para que aproveches todas las funciones de la plataforma.',
    icon: <Sparkles className="w-8 h-8" />,
    highlight: null,
    action: null,
    position: 'center'
  },
  {
    id: 'new-contract',
    title: 'Crear Contratos con IA',
    description: 'Haz clic en "Nuevo Contrato" para crear documentos legales personalizados con asistencia de inteligencia artificial. Responde unas preguntas simples y la IA generará tu contrato.',
    icon: <FileText className="w-8 h-8" />,
    highlight: '[data-testid="new-contract-btn"]',
    targetPage: '/dashboard',
    position: 'bottom'
  },
  {
    id: 'recommend',
    title: '¿No sabes qué contrato necesitas?',
    description: 'Usa el botón "¿Qué Necesitas?" y nuestro sistema de IA te preguntará sobre tu situación y recomendará el contrato perfecto para ti.',
    icon: <Sparkles className="w-8 h-8" />,
    highlight: '[data-testid="recommend-btn"]',
    targetPage: '/dashboard',
    position: 'bottom'
  },
  {
    id: 'templates',
    title: 'Biblioteca de 198 Plantillas',
    description: 'Accede a nuestra biblioteca con 198 contratos legales chilenos. Desde compraventa hasta contratos laborales, tenemos todo lo que necesitas.',
    icon: <BookOpen className="w-8 h-8" />,
    highlight: '[data-testid="templates-btn"]',
    targetPage: '/dashboard',
    position: 'bottom'
  },
  {
    id: 'ai-features',
    title: 'Análisis Inteligente',
    description: 'Cada contrato puede ser analizado por IA para detectar:\n• Riesgo de litigio\n• Cláusulas desfavorables\n• Sesgos legales\n• Simplificación de lenguaje',
    icon: <BarChart3 className="w-8 h-8" />,
    highlight: null,
    position: 'center'
  },
  {
    id: 'signatures',
    title: 'Firmas Digitales Seguras',
    description: 'Firma tus contratos de forma digital con validez legal. Puedes usar:\n• Firma manual (dibujar)\n• Firma biométrica (FaceID/TouchID)\n• Multi-firmantes (invitar a otros)',
    icon: <PenTool className="w-8 h-8" />,
    highlight: null,
    position: 'center'
  },
  {
    id: 'referrals',
    title: 'Gana Premium Gratis',
    description: '¡Invita amigos y gana meses de Premium gratis! Cuando tu referido se suscriba, recibirás 1 mes de acceso Premium sin costo.',
    icon: <Gift className="w-8 h-8" />,
    highlight: '[data-testid="referrals-btn"]',
    targetPage: '/dashboard',
    position: 'right'
  },
  {
    id: 'assistant',
    title: 'Asistente Virtual 24/7',
    description: 'Tu asistente de IA está disponible en todo momento. Haz clic en el botón flotante para:\n• Preguntas sobre contratos\n• Asesoría sobre áreas del derecho\n• Ayuda con la plataforma',
    icon: <Shield className="w-8 h-8" />,
    highlight: '[data-testid="virtual-assistant-btn"]',
    position: 'left'
  },
  {
    id: 'complete',
    title: '¡Listo para comenzar!',
    description: 'Ya conoces las funciones principales de NotaryOS. Recuerda que el Asistente Virtual está siempre disponible para ayudarte. ¡Éxito en tu gestión legal!',
    icon: <PartyPopper className="w-8 h-8" />,
    highlight: null,
    position: 'center',
    isFinal: true
  }
];

const OnboardingTour = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [highlightPosition, setHighlightPosition] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const step = TOUR_STEPS[currentStep];
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

  useEffect(() => {
    // Update highlight position when step changes
    if (step.highlight) {
      const element = document.querySelector(step.highlight);
      if (element) {
        const rect = element.getBoundingClientRect();
        setHighlightPosition({
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16
        });
        
        // Scroll element into view
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        setHighlightPosition(null);
      }
    } else {
      setHighlightPosition(null);
    }
  }, [currentStep, step.highlight, location.pathname]);

  // Navigate to target page if needed
  useEffect(() => {
    if (step.targetPage && location.pathname !== step.targetPage) {
      navigate(step.targetPage);
    }
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    // Mark tour as completed in localStorage
    localStorage.setItem('notaryos_tour_completed', 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  if (!isVisible) return null;

  const getModalPosition = () => {
    switch (step.position) {
      case 'center':
        return 'items-center justify-center';
      case 'bottom':
        return 'items-end justify-center pb-32';
      case 'left':
        return 'items-center justify-start pl-8';
      case 'right':
        return 'items-center justify-end pr-8';
      default:
        return 'items-center justify-center';
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-[100]"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Highlight Box */}
          {highlightPosition && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed z-[101] border-2 border-blue-500 rounded-xl pointer-events-none"
              style={{
                top: highlightPosition.top,
                left: highlightPosition.left,
                width: highlightPosition.width,
                height: highlightPosition.height,
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.7), 0 0 20px rgba(59, 130, 246, 0.5)'
              }}
            >
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="absolute inset-0 border-2 border-blue-400 rounded-xl"
              />
            </motion.div>
          )}

          {/* Modal */}
          <div className={`fixed inset-0 z-[102] flex ${getModalPosition()} p-4 pointer-events-none`}>
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className="bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 max-w-md w-full pointer-events-auto"
              data-testid="onboarding-modal"
            >
              {/* Progress Bar */}
              <div className="h-1 bg-slate-800 rounded-t-2xl overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-blue-500 to-cyan-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>

              {/* Header */}
              <div className="p-6 pb-0">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center text-white">
                      {step.icon}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">
                        Paso {currentStep + 1} de {TOUR_STEPS.length}
                      </p>
                      <h3 className="text-xl font-bold text-white font-['Outfit']">
                        {step.id === 'welcome' && user?.username 
                          ? `¡Bienvenido, ${user.username}!`
                          : step.title}
                      </h3>
                    </div>
                  </div>
                  <button
                    onClick={handleSkip}
                    className="text-slate-500 hover:text-white transition-colors p-1"
                    data-testid="onboarding-skip-btn"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-slate-300 whitespace-pre-line leading-relaxed">
                  {step.description}
                </p>

                {step.isFinal && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-xl">
                    <div className="flex items-center space-x-2 text-blue-400">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Tour completado</span>
                    </div>
                    <p className="text-sm text-slate-400 mt-2">
                      Puedes volver a ver este tour desde tu perfil si lo necesitas.
                    </p>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="p-6 pt-0 flex items-center justify-between">
                <button
                  onClick={handleSkip}
                  className="text-sm text-slate-500 hover:text-white transition-colors"
                >
                  Saltar tour
                </button>

                <div className="flex items-center space-x-2">
                  {currentStep > 0 && (
                    <button
                      onClick={handlePrev}
                      className="flex items-center space-x-1 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
                      data-testid="onboarding-prev-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span>Anterior</span>
                    </button>
                  )}
                  <button
                    onClick={handleNext}
                    className="flex items-center space-x-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white rounded-lg transition-colors"
                    data-testid="onboarding-next-btn"
                  >
                    <span>{step.isFinal ? 'Comenzar' : 'Siguiente'}</span>
                    {step.isFinal ? <ArrowRight className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Step Indicators */}
              <div className="px-6 pb-6 flex justify-center space-x-1.5">
                {TOUR_STEPS.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentStep(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === currentStep
                        ? 'bg-blue-500 w-6'
                        : idx < currentStep
                        ? 'bg-blue-500/50'
                        : 'bg-slate-700'
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default OnboardingTour;
